"use client";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { EASE } from "./constants";
import { CaptureLeads } from "./scenes/capture-leads";
import { FillClasses } from "./scenes/fill-classes";
import { GrowLocations } from "./scenes/grow-locations";
import { ConvertTrials } from "./scenes/intro-offer";
import { MembershipJourney } from "./scenes/membership-journey";
import type { Tab } from "./types";

const TABS: Tab[] = [
  { id: "capture", label: "Message every lead", Scene: CaptureLeads },
  { id: "convert", label: "Intro offer to member", Scene: ConvertTrials },
  { id: "fill", label: "Fill classes", Scene: FillClasses },
  { id: "retain", label: "Membership lifecycle", Scene: MembershipJourney },
  { id: "grow", label: "Grow locations", Scene: GrowLocations },
];

// Glassy circular transport button used in the paused overlay. `size="lg"` is
// the primary play affordance; the smaller prev/next flank it.
function OverlayButton({
  onClick,
  label,
  size = "sm",
  children,
}: {
  onClick: () => void;
  label: string;
  size?: "sm" | "lg";
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        "pointer-events-auto flex items-center justify-center rounded-full border border-border bg-background/90 text-muted-foreground shadow-xl backdrop-blur-md transition-[transform,color] hover:scale-105 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-white/10 dark:bg-black/70",
        size === "lg" ? "size-14" : "size-9",
      )}
    >
      {children}
    </button>
  );
}

export function UseCaseTabs() {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const reduceMotion = useReducedMotion() ?? false;
  const Scene = TABS[active].Scene;
  const scrollerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Mirror `playing` in a ref so the stable scene-complete callback can read it
  // without re-subscribing.
  const playingRef = useRef(true);

  const advance = useCallback(() => setActive((i) => (i + 1) % TABS.length), []);

  // Keep the active pill centered within the horizontal strip — important when
  // scenes auto-advance and the next tab is off-screen on narrow viewports.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const el = tabRefs.current[active];
    if (!scroller || !el) return;
    const sRect = scroller.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const delta = eRect.left - sRect.left - (scroller.clientWidth - el.clientWidth) / 2;
    scroller.scrollTo({
      left: scroller.scrollLeft + delta,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  }, [active, reduceMotion]);

  // When a scene finishes playing it advances the strip to the next use case,
  // looping back to the first — an auto-playing showcase. Disabled for reduced
  // motion (scenes render their final state instantly) and while paused.
  const handleSceneComplete = useCallback(() => {
    if (!playingRef.current) return;
    advance();
  }, [advance]);

  // Selecting a tab resumes playback so the chosen scene plays through — a
  // paused scene would otherwise sit frozen on its first frame.
  const selectTab = useCallback((next: number | ((i: number) => number)) => {
    playingRef.current = true;
    setPlaying(true);
    setActive((prev) => (typeof next === "function" ? next(prev) : next));
  }, []);

  // Bumped each time the scene resumes so we can flash a one-shot "pause" icon
  // (YouTube-style) that animates out, distinct from the persistent play button.
  const [resumePulse, setResumePulse] = useState(0);

  const togglePlay = useCallback(() => {
    const next = !playingRef.current;
    playingRef.current = next;
    setPlaying(next);
    if (next) setResumePulse((n) => n + 1);
  }, []);

  // Step between scenes from the paused overlay without resuming, so the
  // controls stay put and the visitor can browse use cases at their own pace.
  const goPrev = useCallback(() => setActive((i) => (i - 1 + TABS.length) % TABS.length), []);
  const goNext = useCallback(() => setActive((i) => (i + 1) % TABS.length), []);

  return (
    <div>
      {/* Tab pills — float and follow on mobile until the section scrolls past */}
      <div className="mb-6 flex justify-center sm:mb-8 max-sm:sticky max-sm:top-[5.75rem] max-sm:z-30 max-sm:transform-gpu">
        <div
          ref={scrollerRef}
          className="flex max-w-full gap-1 overflow-x-auto rounded-full border border-border bg-muted/40 p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden max-sm:bg-muted max-sm:shadow-lg"
        >
          {TABS.map((t, i) => (
            <button
              key={t.id}
              type="button"
              ref={(node) => {
                tabRefs.current[i] = node;
              }}
              onClick={() => selectTab(i)}
              className={cn(
                "relative shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors sm:px-4",
                i === active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {i === active ? (
                <motion.span
                  layoutId="usecase-pill"
                  transition={
                    reduceMotion ? { duration: 0 } : { type: "spring", bounce: 0.2, duration: 0.5 }
                  }
                  className="absolute inset-0 rounded-full border border-border bg-background shadow-sm"
                />
              ) : null}
              <span className="relative z-10">{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Stage */}
      <div className="relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={TABS[active].id}
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="relative lg:h-[520px]"
          >
            <Scene reduce={reduceMotion} paused={!playing} onComplete={handleSceneComplete} />
          </motion.div>
        </AnimatePresence>

        {/* Click anywhere over the scene to pause/resume — YouTube-style. */}
        <button
          type="button"
          onClick={togglePlay}
          aria-label={playing ? "Pause" : "Play"}
          className="absolute inset-0 z-20 cursor-pointer focus-visible:outline-none"
        >
          {/* Subtle scrim that fades in while paused to lift the controls. */}
          <span
            aria-hidden
            className={cn(
              "absolute inset-0 bg-background/30 backdrop-blur-[1px] transition-opacity duration-300 dark:bg-black/30",
              playing ? "opacity-0" : "opacity-100",
            )}
          />
        </button>

        {/* Transport — visible while paused: prev · play · next. Sits above the
            click target; empty space stays click-through so the scene toggles. */}
        {!playing ? (
          <motion.div
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, ease: EASE }}
            className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center gap-4"
          >
            <OverlayButton onClick={goPrev} label="Previous use case">
              <ChevronLeft className="size-4" />
            </OverlayButton>
            <OverlayButton onClick={togglePlay} label="Play" size="lg">
              <Play className="size-6 translate-x-0.5 fill-current" />
            </OverlayButton>
            <OverlayButton onClick={goNext} label="Next use case">
              <ChevronRight className="size-4" />
            </OverlayButton>
          </motion.div>
        ) : null}

        {/* One-shot pause flash on resume — ripples out and fades. */}
        {resumePulse > 0 ? (
          <motion.span
            key={resumePulse}
            aria-hidden
            initial={{ opacity: 0.9, scale: 0.7 }}
            animate={{ opacity: 0, scale: 1.35 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="pointer-events-none absolute left-1/2 top-1/2 z-30 flex size-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-background/90 text-foreground shadow-xl backdrop-blur-md dark:bg-black/70"
          >
            <Pause className="size-6 fill-current" />
          </motion.span>
        ) : null}
      </div>
    </div>
  );
}
